<script lang="ts" setup>
  import type { ICurrencyData } from '~/store/cashier';

  const props = defineProps<{
    currency: ICurrencyData | undefined;
    exchangeRate: number | undefined;
  }>();

  const calcAmountUSD = ref<number | ''>('');
  const calcAmountCrypto = computed({
    get: () => {
      if (calcAmountUSD.value === '') {
        return '';
      }

      if (!props.exchangeRate || !calcAmountUSD.value) {
        return 0;
      }

      const result = calcAmountUSD.value / props.exchangeRate;
      return Number.isFinite(result) ? Number.parseFloat(result.toFixed(8)) : '';
    },
    set: (newValue) => {
      const numericValue = Number.parseFloat(`${newValue}`);

      if (!Number.isNaN(numericValue) && props.exchangeRate !== undefined) {
        calcAmountUSD.value = numericValue * props.exchangeRate;
      } else {
        calcAmountUSD.value = 0;
      }
    },
  });

  const currencyIconUrl = computed(() => `/images/currencies/${props.currency?.icon || ''}`);

  function sanitizeInputPositiveNumbers(event: KeyboardEvent) {
    if (!event.target) return;

    if (
      ['Delete', 'Backspace', 'Tab', 'Escape', 'Enter'].includes(event.code) ||
      (event.code === 'KeyA' && (event.ctrlKey || event.metaKey)) ||
      (event.code === 'KeyC' && (event.ctrlKey || event.metaKey)) ||
      (event.code === 'KeyV' && (event.ctrlKey || event.metaKey)) ||
      ['Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp'].includes(event.code)
    ) {
      return;
    }

    const isDigit = event.key >= '0' && event.key <= '9';
    const isDecimalSeparator = event.key === '.' || event.key === ',';
    const target = event.target as HTMLInputElement;
    const value = target.value;

    if (isDecimalSeparator && (value.includes('.') || value.includes(','))) {
      event.preventDefault();
    } else if (!isDigit && !isDecimalSeparator) {
      event.preventDefault();
    }
  }
</script>

<template>
  <div class="flex flex-col gap-2">
    <p class="text-white font-barlow text-sm font-medium leading-[120%]">Calculator</p>
    <div class="flex gap-3">
      <UInput
        v-model.number="calcAmountUSD"
        :ui="{ base: 'peer h-full pt-4 rounded-md' }"
        class="flex h-[42px] p-0 flex-col justify-center items-start basis-full bg-white rounded-md"
        placeholder=""
        type="number"
        variant="subtle"
        @keydown="sanitizeInputPositiveNumbers"
      >
        <label
          :class="{
            'text-sm top-[15px] left-2.5': !calcAmountUSD && calcAmountUSD !== 0,
            'text-[10px] top-1.5 left-2.5': calcAmountUSD || calcAmountUSD === 0,
          }"
          class="pointer-events-none absolute p-0 font-barlow font-medium leading-3 transition-all peer-focus:text-[10px] peer-focus:top-1.5 peer-focus:left-2.5"
        >
          <span class="text-[#101317]">($) USD</span>
        </label>
      </UInput>

      <UInput
        v-model.number="calcAmountCrypto"
        :ui="{ base: 'peer h-full pt-4 rounded-md' }"
        class="flex h-[42px] p-0 flex-col justify-center items-start basis-full bg-white rounded-md"
        placeholder=""
        type="number"
        variant="subtle"
        @keydown="sanitizeInputPositiveNumbers"
      >
        <label
          :class="{
            'text-sm top-[15px] left-2.5': !calcAmountCrypto && calcAmountCrypto !== 0,
            'text-[10px] top-1.5 left-2.5': calcAmountCrypto || calcAmountCrypto === 0,
          }"
          class="pointer-events-none absolute p-0 font-barlow font-medium leading-3 transition-all peer-focus:text-[10px] peer-focus:top-1.5 peer-focus:left-2.5"
        >
          <span class="flex items-center gap-0.5">
            <img
              :alt="`${currency?.text} icon`"
              :class="{
                'size-2': calcAmountCrypto || calcAmountCrypto === 0,
                'size-4': !calcAmountCrypto && calcAmountCrypto !== 0,
              }"
              :src="currencyIconUrl"
            />
            <span class="text-[#101317]">
              {{ currency?.text }}
            </span>
          </span>
        </label>
      </UInput>
    </div>
  </div>
</template>
